import {
  Calendar,
  CheckCircle,
  CreditCard,
  DollarSign,
  FileText,
  User,
  XCircle,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import type { CreditAnalysisStatus } from '@/api/commercial/credit-analysis';
import { NewAnalysisConfirmationDialog } from '@/components/dialogs/commercial/credit-analysis/new-analysis-confirmation-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { centsToPtbr } from '@/lib/formatters';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  SEM_RESTRICAO: 'Sem restrição',
  APENAS_A_VISTA: 'Somente à vista',
};

interface CreditAnalysisVerdictDetailsProps {
  creditAnalysis: {
    status: CreditAnalysisStatus;
    paymentMethods?: string[];
    creditLimitInCents?: number;
    creditAvailableInCents?: number;
    newAnalysisIn?: string | 'NEXT_PURCHASE';
    observations?: string;
    analystName?: string;
  };
  onRequestNewAnalysis?: () => void;
  isCreatingNewAnalysis?: boolean;
}

const verdictConfig: Record<
  'APPROVED' | 'PARTIALLY_APPROVED' | 'DECLINED',
  {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    bgColor: string;
    textColor: string;
    borderColor: string;
  }
> = {
  APPROVED: {
    title: 'Análise Aprovada',
    icon: CheckCircle,
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
  },
  PARTIALLY_APPROVED: {
    title: 'Parcialmente Aprovada',
    icon: CheckCircle,
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200',
  },
  DECLINED: {
    title: 'Análise Reprovada',
    icon: XCircle,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
  },
};

export function CreditAnalysisVerdictDetails({
  creditAnalysis,
  onRequestNewAnalysis,
  isCreatingNewAnalysis = false,
}: CreditAnalysisVerdictDetailsProps) {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const isApproved = creditAnalysis.status === 'APPROVED';
  const isPartiallyApproved = creditAnalysis.status === 'PARTIALLY_APPROVED';
  const isDeclined = creditAnalysis.status === 'DECLINED';

  const config =
    verdictConfig[
      isApproved ? 'APPROVED' : isPartiallyApproved ? 'PARTIALLY_APPROVED' : 'DECLINED'
    ];
  const StatusIcon = config.icon;

  const handleRequestNewAnalysisWithConfirmation = () => {
    setShowConfirmationDialog(true);
  };

  const handleConfirmNewAnalysis = () => {
    setShowConfirmationDialog(false);
    onRequestNewAnalysis?.();
  };

  const formatDate = (date: string | 'NEXT_PURCHASE' | undefined) => {
    if (date === 'NEXT_PURCHASE') {
      return 'Próxima Venda';
    }
    if (!date) {
      return '-';
    }
    try {
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) {
        return '-';
      }
      return parsed.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const formatCurrency = (cents: number | null | undefined) => {
    if (cents == null || cents === 0) return '-';
    return centsToPtbr(cents);
  };

  const isExpired = () => {
    if (creditAnalysis.newAnalysisIn === 'NEXT_PURCHASE') {
      return false;
    }
    if (creditAnalysis.newAnalysisIn) {
      return new Date() > new Date(creditAnalysis.newAnalysisIn);
    }
    return false;
  };

  const expired = isExpired();

  return (
    <Card className={`${config.bgColor} ${config.borderColor} border-2`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className={`${config.textColor} flex items-center gap-2`}>
            <StatusIcon className="h-6 w-6" />
            {config.title}
          </CardTitle>
          {creditAnalysis.analystName && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="h-4 w-4" />
              <span>Analista: {creditAnalysis.analystName}</span>
            </div>
          )}
        </div>
        {(expired || isDeclined) && onRequestNewAnalysis && (
          <Button
            onClick={handleRequestNewAnalysisWithConfirmation}
            variant={isDeclined ? 'destructive' : 'outline'}
            size="sm"
            className="mt-4"
            disabled={isCreatingNewAnalysis}
          >
            {isCreatingNewAnalysis
              ? 'Criando...'
              : expired
                ? 'Nova Análise'
                : 'Solicitar Nova Análise'}
          </Button>
        )}
      </CardHeader>

      <CardContent className="grid grid-cols-2 gap-4">
        {/* Payment Methods */}
        {creditAnalysis.paymentMethods && creditAnalysis.paymentMethods.length > 0 && (
          <div className="flex items-start gap-3">
            <CreditCard className={`h-5 w-5 ${config.textColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className="font-medium text-sm text-gray-700">Formas de Pagamento</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {creditAnalysis.paymentMethods.map((method) => (
                  <span
                    key={method}
                    className={`${config.bgColor} ${config.textColor} ${config.borderColor} px-2 py-1 rounded-md text-xs font-medium border`}
                  >
                    {PAYMENT_METHOD_LABELS[method] || method}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Credit Limit */}
        {(isApproved || isPartiallyApproved) && (
          <div className="flex items-start gap-3">
            <DollarSign className={`h-5 w-5 ${config.textColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className="font-medium text-sm text-gray-700">Limite de Crédito</p>
              <p className={`text-lg font-semibold ${config.textColor}`}>
                {formatCurrency(creditAnalysis.creditLimitInCents)}
              </p>
            </div>
          </div>
        )}

        {/* Credit Available */}
        {(isApproved || isPartiallyApproved) && (
          <div className="flex items-start gap-3">
            <DollarSign className={`h-5 w-5 ${config.textColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className="font-medium text-sm text-gray-700">Limite Disponível</p>
              <p className={`text-lg font-semibold ${config.textColor}`}>
                {formatCurrency(creditAnalysis.creditAvailableInCents)}
              </p>
            </div>
          </div>
        )}

        {/* Validity Date */}
        {creditAnalysis.newAnalysisIn && (
          <div className="flex items-start gap-3">
            <Calendar className={`h-5 w-5 ${config.textColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className="font-medium text-sm text-gray-700">Válido até</p>
              <p className={`text-sm ${config.textColor} ${expired ? 'line-through' : ''}`}>
                {formatDate(creditAnalysis.newAnalysisIn)}
              </p>
              {expired && <p className="text-xs text-red-600 mt-1">Esta análise expirou</p>}
            </div>
          </div>
        )}

        {/* Observations */}
        {creditAnalysis.observations && (
          <div className="flex items-start gap-3 col-span-2">
            <FileText className={`h-5 w-5 ${config.textColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className="font-medium text-sm text-gray-700">Observações</p>
              <p className="text-sm text-gray-600 mt-1">{creditAnalysis.observations}</p>
            </div>
          </div>
        )}

        {/* Declined Reason */}
        {isDeclined && !creditAnalysis.observations && (
          <div
            className={`p-3 rounded-lg col-span-2 ${config.bgColor} ${config.borderColor} border`}
          >
            <p className="text-sm font-medium">
              Entre em contato com o setor comercial para mais informações sobre a reprovação.
            </p>
          </div>
        )}
      </CardContent>

      {/* Confirmation Modal */}
      <NewAnalysisConfirmationDialog
        isOpen={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={handleConfirmNewAnalysis}
        isPending={isCreatingNewAnalysis}
      />
    </Card>
  );
}
